import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Editor from "@monaco-editor/react";
import { Spinner } from "react-bootstrap";
import {
  FaCheckCircle, FaTimesCircle, FaPlay, FaTerminal,
  FaCloudUploadAlt, FaAngleDown, FaChevronUp, FaChevronDown,
  FaLightbulb, FaListOl, FaKeyboard, FaCode, FaClock,
  FaExclamationTriangle
} from "react-icons/fa";
import CommonToaster from "../../Common/CommonToaster";
import apiClient from "../../../utils/apiClient";
import { getCurrentUser } from "../../../utils/auth";
import { fetchJudgeLanguages, toSandboxLanguages, normalizeSampleCode } from "../../../utils/judgeLanguages";
import "../../Practice/practice.scss";
import "./assignmentStyles.scss";

const TABS = [
  { key: "input", label: "Input", icon: <FaKeyboard /> },
  { key: "output", label: "Output", icon: <FaTerminal /> },
  { key: "results", label: "Test Results", icon: <FaListOl /> },
];

const StudentAssignmentSessionPage = () => {
  const { id: sessionId } = useParams();
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const isDevMode = currentUser?.mode === "dev" || false;
  const editorRef = useRef(null);

  /* ── Session state ── */
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [submittedQuestions, setSubmittedQuestions] = useState({});

  /* ── Per-question code & language maps ── */
  const [codeMap, setCodeMap] = useState({});
  const [langMap, setLangMap] = useState({});

  /* ── Editor / IO state ── */
  const [languages, setLanguages] = useState([]);
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [results, setResults] = useState([]);
  const [running, setRunning] = useState(false);

  /* ── Bottom panel ── */
  const [activeTab, setActiveTab] = useState("input");
  const [bottomOpen, setBottomOpen] = useState(true);
  const [leftWidth, setLeftWidth] = useState(38);

  /* ── Security state ── */
  const [timeLeft, setTimeLeft] = useState(0);
  const [tabSwitches, setTabSwitches] = useState(0);

  /* ── Toast ── */
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const formatTime = (secs) => {
    const m = String(Math.floor(secs / 60)).padStart(2, "0");
    const s = String(secs % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  /* ════════════════════════════════════════════
     Data fetching
     ════════════════════════════════════════════ */

  useEffect(() => {
    fetchJudgeLanguages().then(raw => setLanguages(toSandboxLanguages(raw)));
  }, []);

  useEffect(() => {
    setLoading(true);
    apiClient.get(`/assignment-sessions/${sessionId}`)
      .then(res => {
        setSession(res.data.session);
        const submitted = {};
        (res.data.session.answers || []).forEach(ans => {
          const qid = ans.question._id || ans.question;
          submitted[qid] = true;
        });
        setSubmittedQuestions(submitted);
      })
      .catch(() => {
        setSession(null);
        setToast({ show: true, message: "Failed to load assignment session", type: "error" });
      })
      .finally(() => setLoading(false));
  }, [sessionId]);

  /* Initialise code/lang for each question once data is ready */
  useEffect(() => {
    if (!session || languages.length === 0) return;
    const initCode = {};
    const initLang = {};
    session.questions.forEach(q => {
      const normalized = normalizeSampleCode(q.sampleCode);
      const available = languages.find(l => normalized?.[l.value]);
      const langVal = available?.value || languages[0]?.value || "";
      initLang[q._id] = langVal;
      initCode[q._id] = { [langVal]: normalized?.[langVal] || "" };
    });
    setCodeMap(initCode);
    setLangMap(initLang);
  }, [session, languages]);

  /* ════════════════════════════════════════════
     Security hooks
     ════════════════════════════════════════════ */

  /* Prevent reload / close */
  useEffect(() => {
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, []);

  /* Prevent back navigation */
  useEffect(() => {
    const h = () => {
      setToast({ show: true, message: "You cannot go back during the assignment.", type: "warning" });
      window.history.pushState(null, "", window.location.href);
    };
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", h);
    return () => window.removeEventListener("popstate", h);
  }, []);

  /* Tab-switch tracking */
  useEffect(() => {
    const onBlur = () => { if (!isDevMode) setTabSwitches(prev => prev + 1); };
    window.addEventListener("blur", onBlur);
    return () => window.removeEventListener("blur", onBlur);
  }, [isDevMode]);

  /* Tab-switch warnings & auto-submit */
  useEffect(() => {
    if (tabSwitches > 2 && tabSwitches < 4) {
      setToast({ show: true, message: "You switched tabs multiple times. Please focus on the assignment.", type: "warning" });
    } else if (tabSwitches === 4) {
      setToast({ show: true, message: "Last warning! Focus on the assignment or it will be auto-submitted.", type: "warning" });
    } else if (tabSwitches > 4) {
      setToast({ show: true, message: "Too many tab switches. Auto-submitting assignment.", type: "error" });
      handleAutoSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabSwitches]);

  /* Timer + auto-submit on expiry */
  useEffect(() => {
    if (!session?.startedAt || !session?.assignment?.timeLimit) return;
    const start = new Date(session.startedAt).getTime();
    const end = start + session.assignment.timeLimit * 60 * 1000;

    const tick = () => {
      const now = Date.now();
      setTimeLeft(Math.max(0, Math.floor((end - now) / 1000)));
      if (now >= end) { clearInterval(iv); handleAutoSubmit(); }
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  /* ════════════════════════════════════════════
     Derived values
     ════════════════════════════════════════════ */

  const currentQ = session?.questions?.[currentIdx];
  const currentLang = currentQ ? (langMap[currentQ._id] || "") : "";
  const currentCode = currentQ ? (codeMap[currentQ._id]?.[currentLang] || "") : "";
  const isCurrentSubmitted = currentQ ? !!submittedQuestions[currentQ._id] : false;
  const testCases = currentQ
    ? (currentQ.testCases || []).filter(tc => !tc.isHidden).map(tc => ({ input: tc.input, expected: tc.expectedOutput }))
    : [];
  const passedCount = results.filter(r => r.passed).length;
  const visibleResults = results.filter(r => !r.isHidden);
  const hiddenResults = results.filter(r => r.isHidden);
  const hiddenPassed = hiddenResults.filter(r => r.passed).length;
  const normalizedCurrent = currentQ ? normalizeSampleCode(currentQ.sampleCode) : {};
  const recommendedLangs = languages.filter(l => normalizedCurrent?.[l.value]);
  const otherLangs = languages.filter(l => !normalizedCurrent?.[l.value]);
  const timerDanger = timeLeft > 0 && timeLeft <= 60;
  const timerWarning = timeLeft > 60 && timeLeft <= 300;

  /* ════════════════════════════════════════════
     Auto-submit (timeout / fraud)
     ════════════════════════════════════════════ */

  const handleAutoSubmit = async () => {
    if (!currentQ) return;
    const langDef = languages.find(l => l.value === currentLang);
    if (!langDef?.id) return;
    const allTcs = currentQ.testCases || [];
    const tcs = allTcs.map(tc => ({ input: tc.input, expected: tc.expectedOutput }));
    try {
      const { data } = await apiClient.post("/code/run-batch", {
        source_code: currentCode,
        language_id: langDef.id,
        testCases: tcs,
      });
      const r = (data.results || []).map((res, i) => ({ ...res, isHidden: !!allTcs[i]?.isHidden }));
      const pc = r.filter(x => x.passed).length;
      await apiClient.post(`/assignment-sessions/${sessionId}/submit`, {
        answers: [{ question: currentQ._id, code: { [currentLang]: currentCode }, results: r, passedCount: pc, total: r.length, status: pc === r.length ? "passed" : "failed", notes: pc === r.length ? "All passed" : `${r.length - pc}/${r.length} failed` }],
      });
      await apiClient.post(`/assignment-sessions/${sessionId}/submit`, { status: "submitted" });
      navigate("/s/assignments");
    } catch {
      setToast({ show: true, message: "Auto-submit failed", type: "error" });
    }
  };

  /* ════════════════════════════════════════════
     Editor actions
     ════════════════════════════════════════════ */

  const handleLangChange = (e) => {
    if (!currentQ) return;
    const v = e.target.value;
    setLangMap(prev => ({ ...prev, [currentQ._id]: v }));
    const norm = normalizeSampleCode(currentQ.sampleCode);
    if (!codeMap[currentQ._id]?.[v]) {
      setCodeMap(prev => ({ ...prev, [currentQ._id]: { ...prev[currentQ._id], [v]: norm?.[v] || "" } }));
    }
    setResults([]);
    setOutput("");
  };

  const handleCodeChange = (newCode) => {
    if (!currentQ) return;
    setCodeMap(prev => ({ ...prev, [currentQ._id]: { ...prev[currentQ._id], [currentLang]: newCode } }));
  };

  const handleRun = async () => {
    setRunning(true);
    setOutput("");
    setActiveTab("output");
    setBottomOpen(true);
    const langId = languages.find(l => l.value === currentLang)?.id;
    if (!langId) { setRunning(false); return; }
    try {
      const { data } = await apiClient.post("/code/run", { source_code: currentCode, language_id: langId, stdin: input });
      setOutput((data.stdout || data.stderr || data.compile_output || "").trim());
    } catch { setOutput("Error running code."); }
    setRunning(false);
  };

  const handleRunAll = async () => {
    if (!currentQ?.testCases?.length) return;
    setRunning(true);
    setResults([]);
    setActiveTab("results");
    setBottomOpen(true);
    const langId = languages.find(l => l.value === currentLang)?.id;
    if (!langId) { setRunning(false); return; }
    const allTcs = currentQ.testCases || [];
    const tcs = allTcs.map(tc => ({ input: tc.input, expected: tc.expectedOutput }));
    try {
      const { data } = await apiClient.post("/code/run-batch", { source_code: currentCode, language_id: langId, testCases: tcs });
      // Tag each result with hidden flag from the original test case
      const taggedResults = (data.results || []).map((r, i) => ({ ...r, isHidden: !!allTcs[i]?.isHidden }));
      setResults(taggedResults);
    } catch {
      setToast({ show: true, message: "Failed to run test cases", type: "error" });
    }
    setRunning(false);
  };

  const handleSubmit = async () => {
    if (!results.length) {
      setToast({ show: true, message: "Run all test cases first", type: "warning" });
      return;
    }
    const pc = results.filter(r => r.passed).length;
    const total = results.length;
    try {
      await apiClient.post(`/assignment-sessions/${sessionId}/submit`, {
        answers: [{ question: currentQ._id, code: { [currentLang]: currentCode }, results, passedCount: pc, total, status: pc === total ? "passed" : "failed", notes: pc === total ? "All test cases passed" : `Failed ${total - pc}/${total}` }],
      });
      setToast({ show: true, message: "Answer submitted!", type: "success" });
      setSubmittedQuestions(prev => {
        const next = { ...prev, [currentQ._id]: true };
        if (session.questions.every(q => next[q._id])) {
          apiClient.post(`/assignment-sessions/${sessionId}/submit`, { status: "submitted" })
            .then(() => setToast({ show: true, message: "Assignment submitted!", type: "success" }));
          navigate("/s/assignments");
        }
        return next;
      });
      const nextIdx = session.questions.findIndex((q, i) => !submittedQuestions[q._id] && i !== currentIdx);
      if (nextIdx !== -1) { setCurrentIdx(nextIdx); setResults([]); setOutput(""); setInput(""); }
    } catch {
      setToast({ show: true, message: "Failed to submit answer", type: "error" });
    }
  };

  const handleQuestionSwitch = (idx) => {
    setCurrentIdx(idx);
    setResults([]);
    setOutput("");
    setInput("");
  };

  /* ════════════════════════════════════════════
     Resizable divider
     ════════════════════════════════════════════ */

  const onDragStart = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = leftWidth;
    const container = e.target.closest(".hr-split-container");
    if (!container) return;
    const totalW = container.getBoundingClientRect().width;
    const onMove = (ev) => {
      const pct = startW + ((ev.clientX - startX) / totalW) * 100;
      setLeftWidth(Math.min(60, Math.max(22, pct)));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [leftWidth]);

  /* ── Editor mount — copy/paste detection ── */
  const onEditorMount = (editor) => {
    editorRef.current = editor;
    editor.focus();
    editor.onKeyDown((event) => {
      if (isDevMode) return;
      const { keyCode, ctrlKey, metaKey } = event;
      if ((keyCode === 33 || keyCode === 52) && (metaKey || ctrlKey)) {
        event.preventDefault();
        setToast({ show: true, message: "Copy/paste is not allowed during the assignment.", type: "warning" });
      }
    });
  };

  /* ════════════════════════════════════════════
     Render
     ════════════════════════════════════════════ */

  if (loading) return <div className="text-center py-5"><Spinner animation="border" /></div>;
  if (!session) return <div className="as-error-state">Could not load assignment session.</div>;
  if (session.status === "submitted") return <div className="as-done-state">Assignment has been submitted.</div>;
  if (session.status === "expired") return <div className="as-done-state as-expired">Assignment has expired.</div>;

  return (
    <div className="hr-practice-page as-session-page user-select-none">
      {/* ── Top Bar ── */}
      <div className="hr-topbar as-topbar">
        <span className="hr-topbar-title">{session.assignment?.title}</span>

        {/* Question navigation */}
        <div className="as-question-nav">
          {session.questions.map((q, idx) => (
            <button
              key={q._id}
              className={`as-qnav-btn ${idx === currentIdx ? "active" : ""} ${submittedQuestions[q._id] ? "submitted" : ""}`}
              onClick={() => handleQuestionSwitch(idx)}
            >
              {submittedQuestions[q._id] && <FaCheckCircle size={9} />}
              {idx + 1}
            </button>
          ))}
        </div>

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {tabSwitches > 0 && (
            <span className={`as-tab-warn ${tabSwitches >= 4 ? "danger" : "warning"}`}>
              <FaExclamationTriangle size={10} />
              {tabSwitches}/5
            </span>
          )}
          <span className={`as-timer ${timerDanger ? "danger" : timerWarning ? "warning" : ""}`}>
            <FaClock size={11} />
            {formatTime(timeLeft)}
          </span>
        </div>
      </div>

      {/* ── Split Container ── */}
      <div className="hr-split-container">
        {/* LEFT — Question */}
        <div className="hr-left-panel" style={{ width: `${leftWidth}%` }}>
          <div className="hr-panel-tabs">
            <span className="hr-panel-tab active"><FaLightbulb size={11} /> Problem</span>
          </div>
          <div className="hr-question-body">
            <h5 className="hr-q-title">{currentQ?.title}</h5>
            <div className="hr-q-desc" dangerouslySetInnerHTML={{ __html: currentQ?.description }} />
            {testCases.length > 0 && (
              <div className="hr-tc-section">
                <div className="hr-tc-heading"><FaListOl size={12} /> Sample Test Cases</div>
                {testCases.map((tc, i) => (
                  <div key={i} className="hr-tc-card">
                    <div className="hr-tc-row">
                      <span className="hr-tc-label">Input</span>
                      <pre className="hr-tc-pre">{tc.input || "(empty)"}</pre>
                    </div>
                    <div className="hr-tc-row">
                      <span className="hr-tc-label">Expected Output</span>
                      <pre className="hr-tc-pre">{tc.expected}</pre>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* DIVIDER */}
        <div className="hr-divider" onMouseDown={onDragStart}>
          <div className="hr-divider-grip" />
        </div>

        {/* RIGHT — Editor + I/O */}
        <div className="hr-right-panel" style={{ width: `${100 - leftWidth}%` }}>
          <div className="hr-editor-toolbar">
            <div className="hr-lang-select-wrap">
              <select
                value={currentLang}
                onChange={handleLangChange}
                className="hr-lang-select"
                disabled={isCurrentSubmitted}
              >
                {recommendedLangs.length > 0 && (
                  <optgroup label="⭐ Recommended">
                    {recommendedLangs.map(l => <option key={l.id} value={l.value}>{l.label}</option>)}
                  </optgroup>
                )}
                <optgroup label="Other Languages">
                  {otherLangs.map(l => <option key={l.id} value={l.value}>{l.label}</option>)}
                </optgroup>
              </select>
              <FaAngleDown className="hr-lang-chevron" />
            </div>
            <div className="hr-toolbar-actions">
              <button className="hr-action-btn run" onClick={handleRun} disabled={running || isCurrentSubmitted}>
                {running ? <Spinner size="sm" animation="border" /> : <><FaPlay size={10} /> Run</>}
              </button>
              <button className="hr-action-btn test" onClick={handleRunAll} disabled={running || isCurrentSubmitted}>
                <FaTerminal size={10} /> Run Tests
              </button>
              <button className="hr-action-btn submit" onClick={handleSubmit} disabled={running || !results.length || isCurrentSubmitted}>
                <FaCloudUploadAlt size={12} /> {isCurrentSubmitted ? "Submitted" : "Submit"}
              </button>
            </div>
          </div>

          <div className="hr-editor-area" style={{ flex: bottomOpen ? "1 1 60%" : "1 1 100%" }}>
            <Editor
              height="100%"
              language={languages.find(l => l.value === currentLang)?.monacoId || "javascript"}
              value={currentCode}
              onChange={(v) => handleCodeChange(v || "")}
              theme="vs-dark"
              onMount={onEditorMount}
              options={{
                fontSize: 14,
                fontFamily: "'Fira Code', 'Consolas', 'Courier New', monospace",
                minimap: { enabled: false },
                wordWrap: "on",
                scrollBeyondLastLine: false,
                padding: { top: 12 },
                formatOnType: true,
                contextmenu: false,
                lineNumbers: "on",
                renderLineHighlight: "line",
                bracketPairColorization: { enabled: true },
                readOnly: isCurrentSubmitted,
              }}
            />
          </div>

          {/* Bottom I/O Panel */}
          <div className={`hr-bottom-panel ${bottomOpen ? "open" : "collapsed"}`}>
            <div className="hr-bottom-header" onClick={() => setBottomOpen(p => !p)}>
              <div className="hr-bottom-tabs">
                {TABS.map(t => (
                  <button
                    key={t.key}
                    className={`hr-bottom-tab ${activeTab === t.key ? "active" : ""}`}
                    onClick={(e) => { e.stopPropagation(); setActiveTab(t.key); setBottomOpen(true); }}
                  >
                    {t.icon} {t.label}
                    {t.key === "results" && results.length > 0 && (
                      <span className={`hr-tab-badge ${passedCount === results.length ? "pass" : "mixed"}`}>
                        {passedCount}/{results.length}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <button className="hr-collapse-btn">
                {bottomOpen ? <FaChevronDown size={11} /> : <FaChevronUp size={11} />}
              </button>
            </div>
            {bottomOpen && (
              <div className="hr-bottom-body">
                {activeTab === "input" && (
                  <textarea
                    className="hr-io-textarea"
                    placeholder="Enter custom input here…"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    spellCheck={false}
                  />
                )}
                {activeTab === "output" && (
                  <pre className="hr-io-output">{output || "Run your code to see output"}</pre>
                )}
                {activeTab === "results" && (
                  <div className="hr-results-list">
                    {results.length === 0 ? (
                      <div className="hr-results-empty">
                        <FaCode size={20} />
                        <span>Run test cases to see results</span>
                      </div>
                    ) : (
                      <>
                        {visibleResults.map((r, i) => (
                          <div key={i} className={`hr-result-row ${r.passed ? "pass" : "fail"}`}>
                            <div className="hr-result-status">
                              {r.passed ? <FaCheckCircle className="text-success" /> : <FaTimesCircle className="text-danger" />}
                              <span>Case {i + 1}</span>
                            </div>
                            <div className="hr-result-data">
                              <div><span className="hr-rd-label">Input</span><code>{r.input || "(empty)"}</code></div>
                              <div><span className="hr-rd-label">Expected</span><code>{r.expected}</code></div>
                              <div><span className="hr-rd-label">Output</span><code>{r.output || "—"}</code></div>
                            </div>
                          </div>
                        ))}
                        {hiddenResults.length > 0 && (
                          <div className={`hr-result-row ${hiddenPassed === hiddenResults.length ? "pass" : "fail"} as-hidden-summary`}>
                            <div className="hr-result-status">
                              {hiddenPassed === hiddenResults.length
                                ? <FaCheckCircle className="text-success" />
                                : <FaTimesCircle className="text-danger" />}
                              <span>Hidden Test Cases</span>
                            </div>
                            <div className="as-hidden-detail">
                              {hiddenPassed} of {hiddenResults.length} hidden test case{hiddenResults.length !== 1 ? "s" : ""} passed
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(t => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default StudentAssignmentSessionPage;
